<script setup lang="ts">
import {
  getExpectedChildCount,
  useDirectoryChildrenQuery,
} from '@/components/document/sub-components/file-manager/data/useDirectoryChildrenQuery';
import { buildBackgroundMenu } from '@/components/document/sub-components/file-manager/logic/core/contextMenuBuilders';
import {
  openContextMenuIfAny,
  useFileManagerContextMenu,
  type ContextMenuHeaderSection,
} from '@/components/document/sub-components/file-manager/logic/core/useFileManagerContextMenu';
import { useFileManagerCreateFolder } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerCreateFolder';
import { useFileManagerNavigation } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerNavigation';
import { useFileManagerSettings } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerSettings';
import { useNativeFileDropTarget } from '@/components/document/sub-components/file-manager/logic/drag-drop/useNativeFileDropTarget';
import { useFileExplorerSelection } from '@/components/document/sub-components/file-manager/logic/selection/useFileManagerSelection';
import { useFileManagerUpload } from '@/components/document/sub-components/file-manager/logic/upload/useFileManagerUpload';
import { useFileManagerActionVisibility } from '@/components/document/sub-components/file-manager/permissions/useFileManagerActionVisibility';
import { DropIndicator } from '@/components/document/sub-components/file-manager/sub-components/drop-indicator';
import { Spinner } from '@/components/document/sub-components/file-manager/sub-components/spinner';
import {
  getParentPath,
  getPathName,
  isAtRoot,
} from '@/components/document/sub-components/file-manager/utils';
import { computed, ref, watch } from 'vue';
import { filterFileSystemItems } from './filterFileSystemItems';
import { getItemIcon } from './sub-components/render-modes/_shared/file-object/sub-components/file-item/get-file-icon';
import { sortFileSystemItems } from './sortFileSystemItems';
import { EmptyFolderNotice } from './sub-components/empty-folder-notice';
import { FileExplorerHandler } from './sub-components/file-explorer-handler';
import { FolderLoadError } from './sub-components/folder-load-error';
import {
  DetailsTableSkeleton,
  DetailsTableView,
} from './sub-components/render-modes/details-table-view';

const { currentPath, navigateTo } = useFileManagerNavigation();
const settings = useFileManagerSettings();

const parentPath = computed(() => getParentPath(currentPath.value));
const atRoot = computed(() => isAtRoot(currentPath.value));

const viewMode = computed({
  get: () => settings.viewMode.value,
  set: (mode) => settings.setViewMode(mode),
});
const iconSize = computed({
  get: () => settings.iconSize.value,
  set: (size) => settings.setIconSize(size),
});

const {
  data: items,
  isLoading,
  isError,
} = useDirectoryChildrenQuery(currentPath, true);
const { clearAll, pruneToVisible } = useFileExplorerSelection();

// Sizes the loading skeleton to the folder's real child count whenever it's already known (seen
// as a row elsewhere — grid or sidebar tree). Falsy (undefined: never seen, or 0: known-empty)
// means there's nothing meaningful to skeleton-shape, so the plain Spinner is used instead.
const expectedChildCount = computed(() =>
  getExpectedChildCount(currentPath.value),
);

const searchQuery = ref('');
watch(currentPath, () => {
  searchQuery.value = '';
  clearAll();
});

const sortedItems = computed(() =>
  sortFileSystemItems(
    filterFileSystemItems(items.value ?? [], searchQuery.value),
  ),
);

watch(sortedItems, (visibleItems) => {
  pruneToVisible(new Set(visibleItems.map((item) => item.path)));
});

// Attached here (rather than on DetailsTableView's own root) because this element is the one
// guaranteed to span the full visible pane via `flex: 1` — the view's own container only sizes to
// fit its content, leaving an uncovered gap below a short list of items where the native browser
// context menu would otherwise still appear.
const { showCreateFolderAt, showCreateFileAt } = useFileManagerActionVisibility();
const contextMenu = useFileManagerContextMenu();
const upload = useFileManagerUpload();
const createFolderState = useFileManagerCreateFolder();

// Names "where a create-folder/create-file action from this menu will land": the current folder's own
// name. At a synthetic level above the mounts both create actions are gated off by
// canCreateFolderAt/canUploadTo, so the menu has no group section and openContextMenuIfAny never
// opens it. (The branch that used to name the project here was already unreachable for that reason,
// and the project is no longer this component's to name.)
//
// The root is the one path this cannot name — there is no last segment — and it is no longer
// unreachable: a host may graft its only mount at '/', which makes the root a writable drive folder
// and opens this menu there. Suppressed rather than labelled: at the root of a single-mount pane
// there is no sibling folder to disambiguate from, which is the header's whole job.
const backgroundMenuHeader = computed<ContextMenuHeaderSection>(() => ({
  kind: 'header',
  ...getItemIcon({ type: 'directory', name: '', path: currentPath.value, hasFolderChildren: false }),
  name: getPathName(currentPath.value),
}));

// openModal()/openFilePicker() default to currentPath when called with no argument, which is
// exactly what "here" means for the grid's own background menu — no need to bind a path explicitly
// the way the tree's background menu (always targeting the project root) has to.
//
// The canCreateFolderAt/canUploadTo checks are the second, path-shaped gate (see
// useFileManagerActionVisibility's comment): where the current path has no drive behind it — a
// virtual root listing the site's document libraries, or a namespace level above them — both entries
// disappear rather than opening a modal whose submit would no-op. A root that IS a mount has a drive,
// so they stay.
const backgroundMenuItems = computed(() =>
  buildBackgroundMenu(
    {
      createFolder:
        showCreateFolderAt(currentPath.value) &&
        createFolderState.canCreateFolderAt(currentPath.value)
          ? { request: createFolderState.openModal }
          : undefined,
      createFile:
        showCreateFileAt(currentPath.value) && upload.canUploadTo(currentPath.value)
          ? { request: upload.openFilePicker }
          : undefined,
    },
    atRoot.value ? undefined : backgroundMenuHeader.value,
  ),
);

const onBackgroundContextMenu = (event: MouseEvent) => {
  if ((event.target as HTMLElement).closest('.details-row')) return;
  const opened = openContextMenuIfAny(
    { x: event.clientX, y: event.clientY },
    backgroundMenuItems.value,
    contextMenu,
  );
  if (opened) {
    event.preventDefault();
    clearAll();
  }
};

const onBackgroundClick = (event: MouseEvent) => {
  if ((event.target as HTMLElement).closest('.details-row')) return;
  clearAll();
};

// OS-file (upload) dropping is wired here rather than on DetailsTableView's own container so the
// drop target spans the full visible pane: that container only sizes to fit its content, so a
// short list of items left the area below it undroppable. This is the same element used to catch
// the background context menu for the same "guaranteed to span via flex: 1" reason. Internal
// item-move drops are still handled per-directory-item inside the view.
const {
  isDragOver: isNativeDragOver,
  onDragEnter: onNativeDragEnter,
  onDragOver: onNativeDragOver,
  onDragLeave: onNativeDragLeave,
  onDrop: onNativeDrop,
} = useNativeFileDropTarget(currentPath);
</script>

<template>
  <div class="file-explorer-view">
    <FileExplorerHandler
      v-model:icon-size="iconSize"
      v-model:search-query="searchQuery"
      :is-at-root="atRoot"
      @back="navigateTo(parentPath)"
    />
    <div class="divider" />
    <div
      class="file-explorer-view-content"
      @contextmenu="onBackgroundContextMenu"
      @click="onBackgroundClick"
      @dragenter.prevent="onNativeDragEnter"
      @dragover="onNativeDragOver"
      @dragleave="onNativeDragLeave"
      @drop.prevent="onNativeDrop"
    >
      <div class="file-explorer-view-scroll">
        <div v-if="isLoading && !expectedChildCount" class="file-explorer-loading">
          <Spinner size="2.5rem" />
        </div>
        <DetailsTableSkeleton
          v-else-if="isLoading"
          :row-count="expectedChildCount ?? 0"
          :icon-size="iconSize"
        />
        <FolderLoadError v-else-if="isError" />
        <!-- Reads `sortedItems`, not the raw query result, so a search that matches nothing gets a
             notice too — with its own wording, since the folder itself isn't empty then. -->
        <EmptyFolderNotice
          v-else-if="sortedItems.length === 0"
          :is-filtered="Boolean(searchQuery.trim())"
        />
        <DetailsTableView
          v-else-if="viewMode === 'details'"
          :items="sortedItems"
          :icon-size="iconSize"
        />
      </div>
      <DropIndicator v-if="isNativeDragOver" />
    </div>
  </div>
</template>

<style scoped>
.file-explorer-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.divider {
  width: 100%;
  flex-shrink: 0;
  border-top: 1px solid #e5e5e5;
}

.file-explorer-view-content {
  flex: 1;
  min-height: 0;
  /* Non-scrolling frame: the positioning context for the DropIndicator overlay, which sits outside
     the inner scroller so it frames the viewport (never scrolls away, never meets the scrollbar).
     The inner .file-explorer-view-scroll owns the scrolling. */
  position: relative;
  /* Bottom corners match the frame (ViewLayout .view-content, 0.75rem); the overlay inherits these
     via `border-radius: inherit`. The top corners butt the header/divider and stay square. */
  border-bottom-left-radius: 0.75rem;
  border-bottom-right-radius: 0.75rem;
}

.file-explorer-view-scroll {
  height: 100%;
  /* Both axes: the details table can be wider than the pane (it grows to fit long file names
     rather than truncating them), so it needs a horizontal scrollbar too. */
  overflow: auto;
}

/* The pane-level drop overlay frames the whole viewport, which includes the strip the details
   table's sticky column header occupies. That header carries `z-index: 1` (so rows scroll under
   it), and neither this element's `position: relative` nor the scroller's `overflow` creates a
   stacking context — so the two compete in the SAME one, where z-index 1 beats the overlay's auto
   no matter which comes later in the DOM. The result was a frame missing its top edge.
   Positioning is the host's job by DropIndicator's own contract, which is why this lives here and
   not in that component (its other hosts — grid rows, sidebar tree rows — have no sticky sibling
   to outrank). Deliberately still below FileExplorerHandler's upload progress panel (z-index: 10),
   which should keep covering the overlay rather than the reverse. */
.file-explorer-view-content :deep(.fm-drop-indicator) {
  z-index: 2;
}

.file-explorer-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}
</style>
